import React, { useEffect, useState, useCallback } from "react";

import { API, BASE_URL } from "../api";

import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiSearch,
  FiX,
  FiSave,
  FiImage,
  FiClipboard,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";
import Pagination from "../components/Pagination";
import Layout from "../components/Layout";

/* ============================================================
   COMPONENT
============================================================ */

const ManageBanners = () => {
  /* ==========================================================
     LIST STATES
  ========================================================== */

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  /* ==========================================================
     MODALS
  ========================================================== */

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [viewBanner, setViewBanner] = useState(null);

  // Delete modal state
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  /* ==========================================================
     EDIT ID
  ========================================================== */

  const [editId, setEditId] = useState(null);

  /* ==========================================================
     LANGUAGES
  ========================================================== */

  const [languages, setLanguages] = useState([]);
  const [languageLoading, setLanguageLoading] = useState(false);

  /* ==========================================================
     FORM
  ========================================================== */

  const initialForm = {
    cmbBannerPage: "0",
    lang_id: "",
    txtDesc: "",
    txtDetailDesc: "",
  };

  const [formData, setFormData] = useState(initialForm);

  /* ==========================================================
     IMAGE
  ========================================================== */

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  /* ==========================================================
     SAVING
  ========================================================== */

  const [saving, setSaving] = useState(false);
  const [imageVersion, setImageVersion] = useState(Date.now());

  /* ==========================================================
     IMAGE URL
  ========================================================== */

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return "";
    }

    let url = "";

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      url = imagePath;
    } else {
      url = `${BASE_URL}/${String(imagePath).replace(/^\/+/, "")}`;
    }

    return `${url}${url.includes("?") ? "&" : "?"}v=${imageVersion}`;
  };

  /* ==========================================================
     FETCH BANNERS
  ========================================================== */

  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);

      const response = await API.get("/banners", {
        params: {
          page,
          limit,
          search,
        },
      });

      const result = response.data || {};

      if (result.success === false) {
        toasts.error(result.message || "Failed to fetch banners");

        setBanners([]);
        setTotal(0);
        setTotalPages(0);

        return;
      }

      setBanners(Array.isArray(result.data) ? result.data : []);

      setTotal(Number(result.total || 0));

      setTotalPages(Number(result.totalPages || 0));
    } catch (error) {
      console.error("Fetch banners error:", error);

      toasts.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to fetch banners"
      );

      setBanners([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  /* ==========================================================
     FETCH LANGUAGES
  ========================================================== */

  const fetchLanguages = useCallback(async () => {
    try {
      setLanguageLoading(true);

      const response = await API.get("/languages");

      const result = response.data;

      let languageList = [];

      if (Array.isArray(result)) {
        languageList = result;
      } else if (Array.isArray(result?.data)) {
        languageList = result.data;
      } else if (Array.isArray(result?.languages)) {
        languageList = result.languages;
      }

      setLanguages(languageList);
    } catch (error) {
      console.error("Fetch languages error:", error);

      toasts.error(
        error?.response?.data?.message || "Failed to fetch languages"
      );

      setLanguages([]);
    } finally {
      setLanguageLoading(false);
    }
  }, []);

  /* ==========================================================
     INITIAL LOAD
  ========================================================== */

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  /* ==========================================================
     CLEANUP IMAGE URL
  ========================================================== */

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  /* ==========================================================
     HANDLE CHANGE
  ========================================================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /* ==========================================================
     HANDLE IMAGE
  ========================================================== */

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    /* --------------------------------------------------------
       SIZE
    -------------------------------------------------------- */

    if (file.size > 2 * 1024 * 1024) {
      toasts.error("Image size must not exceed 2 MB");

      e.target.value = "";

      return;
    }

    /* --------------------------------------------------------
       TYPE
    -------------------------------------------------------- */

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
    ];

    if (!allowedTypes.includes(file.type)) {
      toasts.error("Only JPG, PNG and GIF images are allowed");

      e.target.value = "";

      return;
    }

    /* --------------------------------------------------------
       PREVIOUS OBJECT URL
    -------------------------------------------------------- */

    if (
      imagePreview &&
      imagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(imagePreview);
    }

    /* --------------------------------------------------------
       SET IMAGE
    -------------------------------------------------------- */

    setImage(file);

    setImagePreview(URL.createObjectURL(file));
  };

  /* ==========================================================
     RESET FORM
  ========================================================== */

  const resetForm = () => {
    if (
      imagePreview &&
      imagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(imagePreview);
    }

    setFormData({
      ...initialForm,
    });

    setImage(null);
    setImagePreview("");
    setEditId(null);
  };

  /* ==========================================================
     OPEN ADD
  ========================================================== */

  const openAddModal = () => {
    resetForm();

    setShowEditModal(false);
    setShowAddModal(true);
  };

  /* ==========================================================
     CLOSE ADD
  ========================================================== */

  const closeAddModal = () => {
    if (saving) {
      return;
    }

    resetForm();

    setShowAddModal(false);
  };

  /* ==========================================================
     OPEN EDIT
  ========================================================== */

  const openEditModal = (banner) => {
    const bannerId = banner?.banner_id;

    if (!bannerId) {
      toasts.error("Invalid banner ID");

      return;
    }

    setEditId(bannerId);

    setFormData({
      cmbBannerPage: String(
        banner.cmbBannerPage ??
          banner.banner_for ??
          "0"
      ),

      lang_id: String(banner.lang_id ?? ""),

      txtDesc:
        banner.txtDesc ??
        banner.banner_title ??
        "",

      txtDetailDesc:
        banner.txtDetailDesc ??
        banner.banner_desc ??
        "",
    });

    setImage(null);

    if (banner.banner_image) {
      setImagePreview(
        getImageUrl(banner.banner_image)
      );
    } else {
      setImagePreview("");
    }

    setShowAddModal(false);
    setShowEditModal(true);
  };

  /* ==========================================================
     CLOSE EDIT
  ========================================================== */

  const closeEditModal = () => {
    if (saving) {
      return;
    }

    resetForm();

    setShowEditModal(false);
  };

  /* ==========================================================
     VALIDATE FORM
  ========================================================== */

  const validateForm = (isEdit = false) => {
    /* --------------------------------------------------------
       BANNER PAGE
    -------------------------------------------------------- */

    if (
      !formData.cmbBannerPage ||
      formData.cmbBannerPage === "0"
    ) {
      toasts.error("Banner page is required");

      return false;
    }

    /* --------------------------------------------------------
       LANGUAGE
    -------------------------------------------------------- */

    if (!formData.lang_id) {
      toasts.error("Language is required");

      return false;
    }

    /* --------------------------------------------------------
       TITLE
    -------------------------------------------------------- */

    if (
      !formData.txtDesc ||
      !formData.txtDesc.trim()
    ) {
      toasts.error("Title is required");

      return false;
    }

    /* --------------------------------------------------------
       DETAIL DESCRIPTION
    -------------------------------------------------------- */

    if (
      !formData.txtDetailDesc ||
      !formData.txtDetailDesc.trim()
    ) {
      toasts.error("Brief description is required");

      return false;
    }

    /* --------------------------------------------------------
       IMAGE
    -------------------------------------------------------- */

    if (!isEdit && !image) {
      toasts.error("Please select a banner image");

      return false;
    }

    return true;
  };

  /* ==========================================================
     CREATE FORM DATA
  ========================================================== */

  const createFormData = () => {
    const data = new FormData();

    data.append(
      "cmbBannerPage",
      formData.cmbBannerPage
    );

    data.append(
      "lang_id",
      formData.lang_id
    );

    data.append(
      "txtDesc",
      formData.txtDesc.trim()
    );

    data.append(
      "txtDetailDesc",
      formData.txtDetailDesc.trim()
    );

    if (image) {
      data.append(
        "txtImageUpload",
        image
      );
    }

    return data;
  };

  /* ==========================================================
     ADD BANNER
  ========================================================== */

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!validateForm(false)) {
      return;
    }

    try {
      setSaving(true);

      const data = createFormData();

      const response = await API.post(
        "/banners",
        data
      );

      if (response.data?.success) {
        toasts.success(
          response.data.message ||
            "Banner added successfully"
        );

        setShowAddModal(false);

        resetForm();

        setPage(1);

        await fetchBanners();
      } else {
        toasts.error(
          response.data?.message ||
            "Unable to add banner"
        );
      }
    } catch (error) {
      console.error(
        "Add banner error:",
        error
      );

      console.error(
        "Status:",
        error?.response?.status
      );

      console.error(
        "Response:",
        error?.response?.data
      );

      toasts.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Unable to add banner"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ==========================================================
     UPDATE BANNER
  ========================================================== */

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!editId) {
      toasts.error("Invalid banner ID");

      return;
    }

    if (!validateForm(true)) {
      return;
    }

    try {
      setSaving(true);

      const data = createFormData();

      const response = await API.put(
        `/banners/${editId}`,
        data
      );

      if (response.data?.success) {
        toasts.success(
          response.data.message ||
            "Banner updated successfully"
        );

        setShowEditModal(false);

        resetForm();

        setImageVersion(Date.now());

        await fetchBanners();
      } else {
        toasts.error(
          response.data?.message ||
            "Unable to update banner"
        );
      }
    } catch (error) {
      console.error(
        "Update banner error:",
        error
      );

      console.error(
        "Status:",
        error?.response?.status
      );

      console.error(
        "Response:",
        error?.response?.data
      );

      if (error?.response?.status === 409) {
        toasts.error(
          error?.response?.data?.message ||
            "Duplicate banner already exists"
        );
      } else {
        toasts.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Unable to update banner"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  /* ==========================================================
     DELETE MODAL
  ========================================================== */

  const openDeleteModal = (banner) => {
    if (!banner?.banner_id) {
      toasts.error("Invalid banner ID");

      return;
    }

    setDelModal(banner);
  };

  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  /* ==========================================================
     DELETE BANNER
  ========================================================== */

  const handleDelete = async () => {
    const id = delModal?.banner_id;

    if (!id) {
      toasts.error("Invalid banner ID");

      return;
    }

    try {
      setDelSaving(true);

      const response = await API.delete(
        `/banners/${id}`
      );

      if (response.data?.success) {
        toasts.success(
          response.data.message ||
            "Banner deleted successfully"
        );

        setDelModal(null);

        if (
          banners.length === 1 &&
          page > 1
        ) {
          setPage(
            (previous) => previous - 1
          );
        } else {
          await fetchBanners();
        }
      } else {
        toasts.error(
          response.data?.message ||
            "Unable to delete banner"
        );
      }
    } catch (error) {
      console.error(
        "Delete banner error:",
        error
      );

      toasts.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Unable to delete banner"
      );
    } finally {
      setDelSaving(false);
    }
  };

  /* ==========================================================
     PAGE NAME
  ========================================================== */

  const getBannerPageName = (value) => {
    if (
      value === 1 ||
      value === "1"
    ) {
      return "Home Page";
    }

    if (
      value === 2 ||
      value === "2"
    ) {
      return "Office Page";
    }

    return value || "—";
  };

  /* ==========================================================
     LANGUAGE NAME
  ========================================================== */

  const getLanguageName = (banner) => {
    if (banner.lang_name) {
      return banner.lang_name;
    }

    const language = languages.find(
      (item) => {
        const id =
          item.lang_id ??
          item.language_id ??
          item.id;

        return (
          String(id) ===
          String(banner.lang_id)
        );
      }
    );

    if (language) {
      return (
        language.lang_name ??
        language.language_name ??
        language.name ??
        banner.lang_id
      );
    }

    return banner.lang_id || "—";
  };

  /* ==========================================================
     SEARCH
  ========================================================== */

  const handleSearch = (e) => {
    setSearch(e.target.value);

    setPage(1);
  };

 // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };

  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <Layout activePage="Manage Banners">
      <main className="dashboard-main">

        {/* ==================================================
            TABLE CARD
        ================================================== */}

        <div className="table-card">

          {/* =================================================
              HEADER
          ================================================= */}

          <div className="table-card-head">

            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard
                size={26}
                className="me-2"
              />

              <span>
                Manage Banners
              </span>
            </h3>

            <div
              className="d-flex align-items-center gap-2"
              style={{
                flexWrap: "wrap",
              }}
            >

              {/* SEARCH */}

              <div className="search-wrapper">

                <span className="search-icon">
                  <FiSearch />
                </span>

                <input
                  type="text"
                  placeholder="Search Banners..."
                  value={search}
                  onChange={handleSearch}
                  className="search-input"
                />

                {search && (
                  <button
                    type="button"
                    className="season-clear-btn"
                    onClick={() => {
                      setSearch("");
                      setPage(1);
                    }}
                  >
                    ✕
                  </button>
                )}

              </div>
            </div>

            {/* ADD */}

            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">
                <FiPlus />
              </span>

              Add Banner
            </button>

          </div>

          {/* =================================================
              TABLE
          ================================================= */}

          <div className="table-responsive">

            <table className="um-table">

              <thead>
                <tr className="um-table-head">

                  <th className="um-th text-center">
                    #
                  </th>

                  <th className="um-th text-center">
                    Banner Image
                  </th>

                  <th className="um-th text-center">
                    Title
                  </th>

                  <th className="um-th text-center">
                    Brief Description
                  </th>

                  <th className="um-th text-center">
                    Language
                  </th>

                  <th className="um-th text-center">
                    Action
                  </th>

                </tr>
              </thead>

              <tbody>

                {/* LOADING */}

                {loading ? (
                  Array.from({
                    length: 5,
                  }).map((_, index) => (
                    <tr key={index}>

                      {Array.from({
                        length: 6,
                      }).map(
                        (_, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="um-td text-center"
                          >
                            <div className="skeleton" />
                          </td>
                        )
                      )}

                    </tr>
                  ))
                ) : banners.length === 0 ? (

                  /* EMPTY */

                  <tr>

                    <td
                      colSpan={6}
                      className="empty-table-cell text-center py-5"
                    >

                      <div className="empty-icon">
                        <FiImage />
                      </div>

                      <div className="empty-title">
                        No banners found
                      </div>

                      <div className="empty-sub">
                        Use the Add Banner button
                        to add one
                      </div>

                    </td>

                  </tr>

                ) : (

                  /* DATA */

                  banners.map(
                    (banner, index) => (
                      <tr
                        key={banner.banner_id}
                        className={`um-row ${
                          index % 2 === 0
                            ? "even-row"
                            : "odd-row"
                        }`}
                      >

                        {/* NUMBER */}

                        <td className="um-td text-center">
                          {(page - 1) *
                            limit +
                            index +
                            1}
                        </td>

                        {/* IMAGE */}

                        <td className="um-td text-center">

                          {banner.banner_image ? (
                            <img
                              src={getImageUrl(
                                banner.banner_image
                              )}
                              alt={
                                banner.banner_title ||
                                "Banner"
                              }
                              crossOrigin="anonymous"
                              style={{
                                width: "150px",
                                height: "55px",
                                objectFit: "cover",
                                borderRadius: "6px",
                              }}
                            />
                          ) : (
                            <div className="empty-icon">
                              <FiImage />
                            </div>
                          )}

                        </td>

                        {/* TITLE */}

                        <td className="um-td text-center">

                          <div className="fw-semibold">
                            {banner.txtDesc ||
                              banner.banner_title ||
                              "—"}
                          </div>

                        </td>

                        {/* DESCRIPTION */}

                        <td className="um-td text-center">

                          {(() => {
                            const description =
                              banner.banner_desc ||
                              banner.txtDetailDesc ||
                              "";

                            if (!description) {
                              return "—";
                            }

                            return description.length >
                              100
                              ? `${description.substring(
                                  0,
                                  100
                                )}...`
                              : description;
                          })()}

                        </td>

                        {/* LANGUAGE */}

                        <td className="um-td text-center">
                          {getLanguageName(
                            banner
                          )}
                        </td>

                        {/* ACTION */}

                        <td className="um-td text-center">

                          <div className="action-buttons">

                            {/* VIEW */}

                            <button
                              type="button"
                              className="action-btn action-view"
                              title="View"
                              onClick={() =>
                                setViewBanner(
                                  banner
                                )
                              }
                            >
                              <FiEye size={15} />
                            </button>

                            {/* EDIT */}

                            <button
                              type="button"
                              className="action-btn action-edit"
                              title="Edit"
                              onClick={() =>
                                openEditModal(
                                  banner
                                )
                              }
                            >
                              <FiEdit size={15} />
                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              className="action-btn action-delete delete-enabled"
                              title="Delete"
                              onClick={() =>
                                openDeleteModal(
                                  banner
                                )
                              }
                            >
                              <FiTrash2 size={15} />
                            </button>

                          </div>

                        </td>

                      </tr>
                    )
                  )
                )}

              </tbody>

            </table>

            {/* =================================================
                PAGINATION
            ================================================= */}

<Pagination
  page={page}
  totalPages={totalPages}
  total={total}
  limit={10}
  loading={loading}
  onPrevious={handlePrevious}
  onNext={handleNext}
/>

          </div>
        </div>

        {/* ==================================================
            ADD / EDIT MODAL
        ================================================== */}

        {(showAddModal ||
          showEditModal) && (
          <div
            className="um-overlay"
            onClick={
              saving
                ? undefined
                : showAddModal
                ? closeAddModal
                : closeEditModal
            }
          >

            <div
              className="um-modal um-view-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              {/* HEADER */}

              <div className="um-modal-head">

                <div className="d-flex align-items-center gap-2">

                  <div className="form-card-icon">
                    {showAddModal ? (
                      <FiPlus size={24} />
                    ) : (
                      <FiEdit size={24} />
                    )}
                  </div>

                  <div>

                    <h2 className="um-modal-title mb-0">
                      {showAddModal
                        ? "Add Banner"
                        : "Edit Banner"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      {showAddModal
                        ? "Create a new banner"
                        : "Update banner information"}
                    </p>

                  </div>

                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={
                    showAddModal
                      ? closeAddModal
                      : closeEditModal
                  }
                  disabled={saving}
                >
                  <FiX />
                </button>

              </div>

              {/* BODY */}

              <div className="um-modal-scroll">

                <form
                  onSubmit={
                    showAddModal
                      ? handleAdd
                      : handleUpdate
                  }
                >

                  <div className="row g-3">

                    {/* BANNER PAGE */}

                    <div className="col-md-4">

                      <label className="field-label">
                        Banner Page *
                      </label>

                      <select
                        name="cmbBannerPage"
                        id="cmbBannerPage"
                        className="rd-input"
                        value={
                          formData.cmbBannerPage
                        }
                        onChange={handleChange}
                      >

                        <option value="0">
                          ---Select---
                        </option>

                        <option value="1">
                          Home Page
                        </option>

                        <option value="2">
                          Office Page
                        </option>

                      </select>

                    </div>

                    {/* LANGUAGE */}

                    <div className="col-md-4">

                      <label className="field-label">
                        Language *
                      </label>

                      <select
                        name="lang_id"
                        id="lang_id"
                        className="rd-input"
                        value={
                          formData.lang_id
                        }
                        onChange={handleChange}
                        disabled={
                          languageLoading
                        }
                      >

                        <option value="">
                          {languageLoading
                            ? "Loading languages..."
                            : "---Select Language---"}
                        </option>

                        {languages.map(
                          (language) => {
                            const languageId =
                              language.lang_id ??
                              language.language_id ??
                              language.id;

                            const languageName =
                              language.lang_name ??
                              language.language_name ??
                              language.name;

                            return (
                              <option
                                key={languageId}
                                value={languageId}
                              >
                                {languageName}
                              </option>
                            );
                          }
                        )}

                      </select>

                    </div>

                    {/* IMAGE */}

                    <div className="col-md-4">

                      <label className="field-label">
                        {showAddModal
                          ? "Image *"
                          : "Change Image"}
                      </label>

                      <input
                        name="txtImageUpload"
                        id="txtImageUpload"
                        type="file"
                        className="rd-input"
                        accept="image/jpeg,image/png,image/gif"
                        onChange={
                          handleImageChange
                        }
                      />

                      <small className="form-card-sub">
                        {showAddModal
                          ? "JPG, PNG or GIF. Maximum 2 MB."
                          : "Leave empty to keep existing image."}
                      </small>

                    </div>

                    {/* TITLE */}

                    <div className="col-md-12">

                      <label className="field-label">
                        Title *
                      </label>

                      <input
                        type="text"
                        name="txtDesc"
                        id="txtDesc"
                        className="rd-input"
                        style={{
                          textAlign: "start",
                        }}
                        value={
                          formData.txtDesc
                        }
                        onChange={
                          handleChange
                        }
                        placeholder="Enter title"
                      />

                    </div>

                    {/* DETAIL DESCRIPTION */}

                    <div className="col-md-12">

                      <label className="field-label">
                        Brief Desc *
                      </label>

                      <textarea
                        name="txtDetailDesc"
                        id="txtDetailDesc"
                        className="rd-input"
                        style={{
                          textAlign: "start",
                        }}
                        rows="4"
                        value={
                          formData.txtDetailDesc
                        }
                        onChange={
                          handleChange
                        }
                        placeholder="Enter brief description"
                      />

                    </div>

                    {/* IMAGE PREVIEW */}

                    {imagePreview && (
                      <div className="col-md-4">

                        <label className="field-label">
                          Banner Image
                        </label>

                        <div className="bg-light rounded-3 border p-3">

                          <img
                            src={
                              imagePreview
                            }
                            alt="Banner"
                            crossOrigin="anonymous"
                            style={{
                              width: "100%",
                              maxHeight:
                                "250px",
                              objectFit:
                                "contain",
                              borderRadius:
                                "6px",
                            }}
                          />

                        </div>

                      </div>
                    )}

                  </div>

                  {/* FOOTER */}

                  <div className="um-modal-foot">

                    <button
                      type="button"
                      className="um-btn-secondary"
                      onClick={
                        showAddModal
                          ? closeAddModal
                          : closeEditModal
                      }
                      disabled={saving}
                    >
                      <FiX />
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="um-btn-primary"
                      disabled={saving}
                    >

                      {saving ? (
                        <span className="spinner" />
                      ) : (
                        <>
                          <FiSave />

                          {showAddModal
                            ? "Save Banner"
                            : "Update Banner"}
                        </>
                      )}

                    </button>

                  </div>

                </form>

              </div>

            </div>

          </div>
        )}

        {/* ==================================================
            VIEW MODAL
        ================================================== */}

        {viewBanner && (
          <div
            className="um-overlay"
            onClick={() =>
              setViewBanner(null)
            }
          >

            <div
              className="um-modal um-view-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              {/* HEADER */}

              <div className="um-modal-head">

                <div>

                  <h2 className="um-modal-title mb-0">
                    <FiImage /> Banner Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    View banner information
                  </p>

                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() =>
                    setViewBanner(null)
                  }
                >
                  <FiX />
                </button>

              </div>

              {/* BODY */}

              <div className="um-modal-scroll">

                {/* IMAGE */}

                {viewBanner.banner_image && (
                  <div className="mb-4">

                    <div className="bg-light rounded-3 border p-3 text-center">

                      <img
                        src={getImageUrl(
                          viewBanner.banner_image
                        )}
                        alt="Banner"
                        crossOrigin="anonymous"
                        style={{
                          width: "100%",
                          maxHeight:
                            "350px",
                          objectFit:
                            "contain",
                          borderRadius:
                            "6px",
                        }}
                      />

                    </div>

                  </div>
                )}

                <div className="row g-3">

                  {/* TITLE */}

                  <div className="col-md-4">

                    <div className="bg-light rounded-3 border p-3 h-100">

                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Title
                      </div>

                      <div className="fw-bold">
                        {viewBanner.txtDesc ||
                          viewBanner.banner_title ||
                          "—"}
                      </div>

                    </div>

                  </div>

                  {/* BANNER PAGE */}

                  <div className="col-md-4">

                    <div className="bg-light rounded-3 border p-3 h-100">

                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Banner Page
                      </div>

                      <div className="fw-bold">

                        {getBannerPageName(
                          viewBanner.cmbBannerPage ??
                            viewBanner.banner_for
                        )}

                      </div>

                    </div>

                  </div>

                  {/* LANGUAGE */}

                  <div className="col-md-4">

                    <div className="bg-light rounded-3 border p-3 h-100">

                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Language
                      </div>

                      <div className="fw-bold">
                        {getLanguageName(
                          viewBanner
                        )}
                      </div>

                    </div>

                  </div>

                  {/* DESCRIPTION */}

                  <div className="col-md-12">

                    <div className="bg-light rounded-3 border p-3">

                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Brief Description
                      </div>

                      <div>
                        {viewBanner.txtDetailDesc ||
                          viewBanner.banner_detaildesc ||
                          viewBanner.banner_desc ||
                          "—"}
                      </div>

                    </div>

                  </div>

                </div>

              </div>

              {/* FOOTER */}

              <div className="um-modal-foot">

                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() =>
                    setViewBanner(null)
                  }
                >
                  <FiX />
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const banner =
                      viewBanner;

                    setViewBanner(null);

                    setTimeout(() => {
                      openEditModal(
                        banner
                      );
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Banner
                </button>

              </div>

            </div>

          </div>
        )}

        {/* ==================================================
            DELETE MODAL
        ================================================== */}

        {delModal && (
          <div
            className="um-overlay"
            onClick={
              delSaving
                ? undefined
                : closeDeleteModal
            }
          >

            <div
              className="um-modal um-delete-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              <div className="delete-content">

                {/* DELETE ICON */}

                <div className="delete-icon">
                  <FiTrash2 size={32} />
                </div>

                {/* DELETE TITLE */}

                <h2 className="delete-title">
                  Delete Banner?
                </h2>

                {/* DELETE DESCRIPTION */}

                <p className="delete-description">

                  Are you sure you want to delete{" "}

                  <span className="badge badge-success">
                    "
                    {delModal.txtDesc ||
                      delModal.banner_title ||
                      "this banner"}
                    "
                  </span>

                  ?

                  <br />

                  This will permanently remove
                  this banner.

                  <br />

                  <strong>
                    This action cannot be undone.
                  </strong>

                </p>

                {/* DELETE ACTIONS */}

                <div className="delete-actions">

                  {/* CANCEL */}

                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={
                      closeDeleteModal
                    }
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  {/* CONFIRM DELETE */}

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={
                      handleDelete
                    }
                  >

                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 />
                        Delete Permanently
                      </>
                    )}

                  </button>

                </div>

              </div>

            </div>

          </div>
        )}


      </main>
    </Layout>
  );
};

export default ManageBanners;